// components/Dashboard.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, setDoc, getDocs, collection, deleteDoc, updateDoc, getDoc, query, where } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const toTitleCase = (str) => str.replace(/(^|\s)\S/g, c => c.toUpperCase());

export default function Dashboard({ user, userName, currentEvent, onEventUpdate }) {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [newEventName, setNewEventName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shareEmail, setShareEmail] = useState('');
  const [shareRole, setShareRole] = useState('visitor');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editEventName, setEditEventName] = useState('');
  const [editEventDate, setEditEventDate] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);

  useEffect(() => {
    if (user) {
      loadEvents();
    }
  }, [user]);

  const LS_KEY = `tenekkapp_last_event_${user?.uid}`;

  const selectEvent = (event) => {
    localStorage.setItem(LS_KEY, event.id);
    onEventUpdate(event);
  };

  const autoSelect = (allEvents) => {
    if (allEvents.length === 0) return;
    if (allEvents.length === 1) { selectEvent(allEvents[0]); return; }
    // Restore last selection if it still exists
    const savedId = localStorage.getItem(LS_KEY);
    const saved = savedId ? allEvents.find(e => e.id === savedId) : null;
    if (saved) { onEventUpdate(saved); return; }
    // Fall back: most recent owned event, then shared
    const sorted = [...allEvents].sort((a, b) => {
      if (!a.isShared && b.isShared) return -1;
      if (a.isShared && !b.isShared) return 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
    selectEvent(sorted[0]);
  };

  const loadEvents = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Load owned events
      const eventsRef = collection(db, 'users', user.uid, 'events');
      const ownedSnapshot = await getDocs(eventsRef);
      const ownedEvents = [];

      for (const docSnap of ownedSnapshot.docs) {
        const eventData = docSnap.data();
        // Migrate old events to include sharedUserIds (async, don't block loading)
        if (eventData.sharedWith && !eventData.sharedUserIds) {
          const sharedUserIds = eventData.sharedWith.map(u => u.userId);
          updateDoc(docSnap.ref, { sharedUserIds }).catch(err =>
            console.warn('Failed to migrate sharedUserIds for event:', docSnap.id, err)
          );
          eventData.sharedUserIds = sharedUserIds;
        }
        ownedEvents.push({ id: docSnap.id, ...eventData });
      }

      // Load shared events
      const sharedEventsRef = collection(db, 'users', user.uid, 'sharedEvents');
      const sharedSnapshot = await getDocs(sharedEventsRef);
      const sharedEvents = [];

      for (const sharedDoc of sharedSnapshot.docs) {
        const sharedData = sharedDoc.data();
        try {
          // Get the actual event data from the owner's collection
          const eventDoc = await getDoc(doc(db, 'users', sharedData.ownerId, 'events', sharedData.eventId));
          if (eventDoc.exists()) {
            const eventData = eventDoc.data();
            sharedEvents.push({
              id: eventDoc.id,
              ...eventData,
              ownerId: sharedData.ownerId,  // always use the authoritative owner UID, not what the doc may/may not store
              isShared: true,
              sharedBy: sharedData.ownerEmail,
              userRole: sharedData.role
            });
          }
        } catch (error) {
          console.error('Error loading shared event:', sharedData.eventId, error);
          // Remove invalid shared event reference
          await deleteDoc(sharedDoc.ref);
        }
      }

      const allEvents = [...ownedEvents, ...sharedEvents];
      setEvents(allEvents);

      // Auto-select: only when nothing is selected yet
      if (!currentEvent) autoSelect(allEvents);
    } catch (error) {
      console.error('Error loading events:', error);
      setError(t('dashboard.load_error'));
    } finally {
      setLoading(false);
    }
  };

  const createEvent = async () => {
    if (!newEventName.trim()) {
      setError(t('dashboard.event_name_required'));
      return;
    }
    if (!user) return;

    setLoading(true);
    const eventId = Date.now().toString();
    const newEvent = {
      name: newEventName,
      createdAt: new Date().toISOString(),
      ownerId: user.uid,
      sharedWith: [],
      sharedUserIds: [],
      guests: [],
      tasks: [],
      services: [],
      gifts: []
    };

    try {
      const eventRef = doc(db, 'users', user.uid, 'events', eventId);
      await setDoc(eventRef, newEvent);
      const eventWithId = { id: eventId, ...newEvent };
      setEvents([...events, eventWithId]);
      selectEvent(eventWithId);
      setNewEventName('');
      setError('');
    } catch (error) {
      console.error('Error creating event:', error);
      setError(t('dashboard.create_error') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (eventId) => {
    setEventToDelete(eventId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteEvent = async () => {
    if (eventToDelete && user) {
      try {
        await deleteDoc(doc(db, 'users', user.uid, 'events', eventToDelete));
        
        // Also delete all shared event references
        const currentEventToDelete = events.find(e => e.id === eventToDelete);
        if (currentEventToDelete?.sharedWith) {
          for (const shared of currentEventToDelete.sharedWith) {
            try {
              await deleteDoc(doc(db, 'users', shared.userId, 'sharedEvents', eventToDelete));
            } catch (error) {
              console.error('Error deleting shared event reference:', error);
            }
          }
        }
        
        setEvents(events.filter(e => e.id !== eventToDelete));
        if (currentEventToDelete?.id === eventToDelete) {
          onEventUpdate(null);
        }
        setShowDeleteConfirm(false);
        setEventToDelete(null);
      } catch (error) {
        console.error('Error deleting event:', error);
        setError('Σφάλμα κατά τη διαγραφή');
        setShowDeleteConfirm(false);
        setEventToDelete(null);
      }
    }
  };

  const shareEvent = async () => {
    if (!shareEmail.trim()) {
      setError(t('dashboard.email_required'));
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(shareEmail.trim())) {
      setError('Παρακαλώ εισάγετε έγκυρο email');
      return;
    }
    
    if (!currentEvent) return;
    
    setLoading(true);
    try {
      // Find user by email
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', shareEmail.trim()));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setError('Δεν βρέθηκε χρήστης με αυτό το email');
        return;
      }
      
      const targetUser = querySnapshot.docs[0];
      const targetUserId = targetUser.id;
      
      if (targetUserId === user.uid) {
        setError('Δεν μπορείτε να μοιραστείτε την εκδήλωση με τον εαυτό σας');
        return;
      }
      
      // Check if already shared
      if (currentEvent.sharedWith?.some(s => s.userId === targetUserId)) {
        setError('Η εκδήλωση είναι ήδη κοινή με αυτόν τον χρήστη');
        return;
      }
      
      const updatedSharedWith = [...(currentEvent.sharedWith || []), {
        userId: targetUserId,
        email: shareEmail.trim(),
        role: shareRole
      }];
      
      const updatedSharedUserIds = [...(currentEvent.sharedUserIds || []), targetUserId];
      
      // Update the owner's event document
      await updateDoc(doc(db, 'users', user.uid, 'events', currentEvent.id), {
        sharedWith: updatedSharedWith,
        sharedUserIds: updatedSharedUserIds
      });
      
      // Add to shared user's sharedEvents subcollection
      const sharedEventRef = doc(db, 'users', targetUserId, 'sharedEvents', currentEvent.id);
      await setDoc(sharedEventRef, {
        eventId: currentEvent.id,
        ownerId: user.uid,
        ownerEmail: user.email,
        role: shareRole,
        sharedAt: new Date().toISOString()
      });
      
      const updatedEvent = { ...currentEvent, sharedWith: updatedSharedWith };
      setEvents(events.map(e => e.id === currentEvent.id ? updatedEvent : e));
      onEventUpdate(updatedEvent);
      
      setShareEmail('');
      setShareRole('visitor');
      setShowShareModal(false);
      setError('');
    } catch (error) {
      console.error('Error sharing event:', error);
      setError(t('dashboard.share_error') + ': ' + error.message);
      setLoading(false);
    }
  };

  const unshareEvent = async (targetUserId) => {
    if (!currentEvent) return;
    
    try {
      const updatedSharedWith = currentEvent.sharedWith.filter(s => s.userId !== targetUserId);
      const updatedSharedUserIds = (currentEvent.sharedUserIds || []).filter(id => id !== targetUserId);
      
      await updateDoc(doc(db, 'users', user.uid, 'events', currentEvent.id), {
        sharedWith: updatedSharedWith,
        sharedUserIds: updatedSharedUserIds
      });
      
      // Remove from shared user's sharedEvents subcollection
      try {
        await deleteDoc(doc(db, 'users', targetUserId, 'sharedEvents', currentEvent.id));
      } catch (deleteError) {
        console.warn('Failed to delete shared event reference:', deleteError);
        // Continue anyway - the event update was successful
      }
      
      const updatedEvent = { ...currentEvent, sharedWith: updatedSharedWith, sharedUserIds: updatedSharedUserIds };
      setEvents(events.map(e => e.id === currentEvent.id ? updatedEvent : e));
      onEventUpdate(updatedEvent);
    } catch (error) {
      console.error('Error unsharing event:', error);
      setError('Σφάλμα κατά την αφαίρεση κοινοποίησης');
    }
  };

  const updateEventDetails = async () => {
    if (!currentEvent || !editEventName.trim()) {
      setError('Παρακαλώ εισάγετε όνομα εκδήλωσης');
      return;
    }
    
    setLoading(true);
    try {
      const updatedEvent = {
        ...currentEvent,
        name: editEventName,
        eventDate: editEventDate || null
      };
      
      // Update owner's document
      const ownerId = currentEvent.ownerId || user.uid;
      await updateDoc(doc(db, 'users', ownerId, 'events', currentEvent.id), {
        name: editEventName,
        eventDate: editEventDate || null
      });
      
      setEvents(events.map(e => e.id === currentEvent.id ? updatedEvent : e));
      onEventUpdate(updatedEvent);
      setShowEditModal(false);
      setError('');
    } catch (error) {
      console.error('Error updating event:', error);
      setError(t('dashboard.update_error') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = () => {
    setEditEventName(currentEvent.name);
    setEditEventDate(currentEvent.eventDate || '');
    setShowEditModal(true);
  };

  const calcBalance = () => {
    if (!currentEvent) return 0;
    const totalIncome = (currentEvent.gifts || []).reduce((sum, g) => sum + (g.amount || 0), 0);
    const totalExpense = (currentEvent.services || []).reduce((sum, s) => sum + (s.cost || 0), 0);
    return totalIncome - totalExpense;
  };

  return (
    <div className="fade-in-up">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}
      
      <div className="card shadow-lg mb-4 border-0">
        <div className="card-body p-4">
          <div className="text-center mb-4">
            <img
              src="/logo.png"
              alt="Tenekkapp Logo"
              style={{ width: '100%', maxWidth: '220px', height: 'auto', objectFit: 'contain' }}
            />
            <h2 className="card-title mb-2 text-primary">
              <i className="fas fa-calendar-alt me-2"></i>{t('dashboard.title')}
            </h2>
            <p className="text-muted mb-0">
              <i className="fas fa-user-circle me-1"></i>
              {t('dashboard.welcome', { email: userName || user?.email })}
            </p>
          </div>
          
          <div className="row g-3">
            <div className="col-12 col-md-8">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="fas fa-plus-circle text-primary"></i>
                </span>
                <input 
                  type="text" 
                  className="form-control border-start-0 ps-0" 
                  placeholder={t('dashboard.event_placeholder')}
                  value={newEventName} 
                  onChange={(e) => setNewEventName(toTitleCase(e.target.value))}
                  onKeyPress={(e) => e.key === 'Enter' && createEvent()}
                />
                <button className="btn btn-primary" onClick={createEvent} disabled={loading}>
                  {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="fas fa-plus me-2"></i>}
                  {t('dashboard.new_event')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-lg-4 order-2 order-lg-1">
          <div className="card shadow-lg mb-4 border-0 h-auto h-lg-100">
            <div className="card-header bg-white border-0 py-3">
              <h5 className="mb-0 text-primary">
                <i className="fas fa-folder-open me-2"></i>
                {t('dashboard.my_events', { count: events.filter(e => !e.isShared).length })}
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {events.filter(e => !e.isShared).map(ev => (
                  <div key={ev.id} className="list-group-item border-0 px-3 py-3">
                    <div className="d-flex justify-content-between align-items-center gap-2">
                      <button 
                        className={`btn btn-link text-start text-decoration-none flex-grow-1 p-0 ${currentEvent?.id === ev.id ? 'fw-bold text-primary' : 'text-dark'}`}
                        onClick={() => selectEvent(ev)}
                      >
                        <div className="d-flex align-items-center">
                          <div className={`rounded-circle me-2 me-md-3 ${currentEvent?.id === ev.id ? 'bg-primary' : 'bg-light'} d-flex align-items-center justify-content-center flex-shrink-0`} style={{width: '40px', height: '40px'}}>
                            <i className={`fas fa-calendar-check ${currentEvent?.id === ev.id ? 'text-white' : 'text-primary'}`}></i>
                          </div>
                          <div className="min-w-0">
                            <div className="fw-semibold text-truncate">{ev.name}</div>
                            <small className="text-muted d-block text-truncate">
                              <i className="fas fa-clock me-1"></i>
                              {new Date(ev.createdAt).toLocaleDateString('el-GR')}
                            </small>
                          </div>
                        </div>
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-danger rounded-pill flex-shrink-0" 
                        onClick={() => deleteEvent(ev.id)}
                        title={t('dashboard.delete_event_title')}
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                ))}
                {events.filter(e => !e.isShared).length === 0 && (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-calendar-times fa-3x mb-3"></i>
                    <p>{t('dashboard.no_events')}</p>
                    <small>{t('dashboard.create_first_event')}</small>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="card shadow-lg mb-4 border-0 h-auto h-lg-100">
            <div className="card-header bg-white border-0 py-3">
              <h5 className="mb-0 text-info">
                <i className="fas fa-share-alt me-2"></i>
                {t('dashboard.shared_events', { count: events.filter(e => e.isShared).length })}
              </h5>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {events.filter(e => e.isShared).map(ev => (
                  <div key={ev.id} className="list-group-item border-0 px-3 py-3">
                    <button 
                      className={`btn btn-link text-start text-decoration-none w-100 p-0 ${currentEvent?.id === ev.id ? 'fw-bold text-info' : 'text-dark'}`}
                      onClick={() => selectEvent(ev)}
                    >
                      <div className="d-flex align-items-center">
                        <div className={`rounded-circle me-2 me-md-3 ${currentEvent?.id === ev.id ? 'bg-info' : 'bg-light'} d-flex align-items-center justify-content-center flex-shrink-0`} style={{width: '40px', height: '40px'}}>
                          <i className={`fas fa-share ${currentEvent?.id === ev.id ? 'text-white' : 'text-info'}`}></i>
                        </div>
                        <div className="flex-grow-1 min-w-0">
                          <div className="fw-semibold text-truncate">{ev.name}</div>
                          <small className="text-muted d-block text-truncate">
                            <i className="fas fa-user-friends me-1"></i>
                            από {ev.sharedBy}
                          </small>
                          <span className={`badge mt-1 ${ev.userRole === 'operator' ? 'bg-warning' : 'bg-info'}`}>
                            <i className={`fas ${ev.userRole === 'operator' ? 'fa-cog' : 'fa-eye'} me-1`}></i>
                            <span className="d-none d-sm-inline">{ev.userRole === 'operator' ? 'Διαχειριστής' : 'Επισκέπτης'}</span>
                            <span className="d-sm-none">{ev.userRole === 'operator' ? 'Δ/Α' : 'Επ'}</span>
                          </span>
                        </div>
                      </div>
                    </button>
                  </div>
                ))}
                {events.filter(e => e.isShared).length === 0 && (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-share fa-3x mb-3"></i>
                    <p>{t('dashboard.no_shared_events')}</p>
                    <small>{t('dashboard.shared_events_empty')}</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-lg-8 order-1 order-lg-2">
          <div className="card shadow-lg border-0">
            <div className="card-body p-3 p-md-4">
              {currentEvent ? (
                <>
                  <div className="text-center mb-4">
                    <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
                      <h3 className="mb-0 text-primary fs-4 fs-md-3">
                        <i className="fas fa-birthday-cake me-2"></i>{currentEvent.name}
                      </h3>
                      {!currentEvent.isShared && (
                        <button 
                          className="btn btn-sm btn-outline-primary rounded-pill" 
                          onClick={openEditModal}
                          title="Επεξεργασία εκδήλωσης"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      )}
                    </div>
                    {currentEvent.eventDate && (
                      <p className="text-muted mb-3 small">
                        <i className="fas fa-calendar me-1"></i>
                        {new Date(currentEvent.eventDate).toLocaleDateString('el-GR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    )}
                    {currentEvent.isShared && (
                      <p className="text-muted mb-3 small">
                        <i className="fas fa-share-alt me-1"></i>
                        {t('dashboard.shared_event_from', { owner: currentEvent.sharedBy })}
                      </p>
                    )}
                  </div>
                  
                  <div className="row g-2 g-md-3 mb-4">
                    <div className="col-6 col-md-4">
                      <Link to="/guests" className="text-decoration-none">
                        <div className="card bg-light border-0 text-center h-100" style={{ background: 'var(--bg-accent)', cursor: 'pointer', transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
                          <div className="card-body d-flex flex-column justify-content-center p-2 p-md-3">
                            <div className="fs-3 fs-md-1 mb-1 mb-md-2 text-primary">
                              <i className="fas fa-users"></i>
                            </div>
                            <div className="fs-5 fs-md-3 fw-bold text-primary mb-1">
                              {(currentEvent.guests?.reduce((sum, g) => sum + (g.adults || 1) + (g.children || 0), 0)) || 0}
                            </div>
                            <div className="text-muted text-truncate" style={{fontSize: '0.75rem'}}>{t('dashboard.total_guests')}</div>
                          </div>
                        </div>
                      </Link>
                    </div>
                    <div className="col-6 col-md-4">
                      <Link to="/tasks" className="text-decoration-none">
                        <div className="card bg-light border-0 text-center h-100" style={{ background: 'var(--bg-accent)', cursor: 'pointer', transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
                          <div className="card-body d-flex flex-column justify-content-center p-2 p-md-3">
                            <div className="fs-3 fs-md-1 mb-1 mb-md-2 text-danger">
                              <i className="fas fa-tools"></i>
                            </div>
                            <div className="fs-5 fs-md-3 fw-bold text-danger mb-1">
                              {currentEvent.services?.length || 0}
                            </div>
                            <div className="text-muted text-truncate" style={{fontSize: '0.75rem'}}>{t('tasks.services_expenses')}</div>
                            <div className="border-top mt-1 pt-1">
                              <div className="fs-6 fw-bold text-success mb-0">
                                {currentEvent.tasks?.filter(t => t.completed).length || 0}/{currentEvent.tasks?.length || 0}
                              </div>
                              <div className="text-muted text-truncate" style={{fontSize: '0.75rem'}}>{t('dashboard.tasks')}</div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                    <div className="col-6 col-md-4">
                      <Link to="/report" className="text-decoration-none">
                        <div className={`card border-0 text-center h-100`} style={{ background: calcBalance() >= 0 ? 'var(--bg-accent)' : '#fef2f2', cursor: 'pointer', transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
                          <div className="card-body d-flex flex-column justify-content-center p-2 p-md-3">
                            <div className={`fs-3 fs-md-1 mb-1 mb-md-2 ${calcBalance() >= 0 ? 'text-warning' : 'text-danger'}`}>
                              <i className="fas fa-euro-sign"></i>
                            </div>
                            <div className={`fs-5 fs-md-3 fw-bold mb-1 ${calcBalance() >= 0 ? 'text-warning' : 'text-danger'}`}>
                              €{calcBalance().toFixed(2)}
                            </div>
                            <div className="text-muted text-truncate" style={{fontSize: '0.75rem'}}>{t('dashboard.balance')}</div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                  
                  {!currentEvent.isShared && (
                    <div className="card border-0 shadow-sm mt-3 mt-md-4" style={{ background: 'var(--bg-secondary)' }}>
                      <div className="card-body p-3 p-md-4">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
                          <h5 className="mb-0 text-primary">
                            <i className="fas fa-share-alt me-2"></i>
                            {t('dashboard.share_event')}
                          </h5>
                          <button 
                            className="btn btn-primary btn-sm rounded-pill px-3" 
                            onClick={() => {
                              setShowShareModal(true);
                              setError(''); // Clear any previous errors
                            }}
                          >
                            <i className="fas fa-plus me-1"></i>
                            {t('dashboard.share_button')}
                          </button>
                        </div>
                        
                        {currentEvent.sharedWith && currentEvent.sharedWith.length > 0 && (
                          <div className="mt-3">
                            <h6 className="text-muted mb-3">
                              <i className="fas fa-users me-1"></i>
                              {t('dashboard.shared_with_count', { count: currentEvent.sharedWith.length })}
                            </h6>
                            <div className="row g-2">
                              {currentEvent.sharedWith.map((shared, index) => (
                                <div key={index} className="col-12 col-sm-6 col-md-6">
                                  <div className="card border-0 shadow-sm h-100" style={{ background: 'rgba(255, 255, 255, 0.8)' }}>
                                    <div className="card-body p-2 p-sm-3">
                                      <div className="d-flex justify-content-between align-items-start gap-2">
                                        <div className="flex-grow-1 min-w-0">
                                          <div className="fw-semibold text-dark mb-1 text-truncate small">
                                            <i className="fas fa-user me-1"></i>
                                            {shared.email}
                                          </div>
                                          <span className={`badge text-nowrap ${shared.role === 'operator' ? 'bg-warning text-dark' : 'bg-info'}`} style={{fontSize: '0.7rem'}}>
                                            <i className={`fas ${shared.role === 'operator' ? 'fa-cog' : 'fa-eye'} me-1`}></i>
                                            <span className="d-none d-sm-inline">{shared.role === 'operator' ? t('dashboard.role_operator') : t('dashboard.role_visitor')}</span>
                                            <span className="d-sm-none">{shared.role === 'operator' ? t('dashboard.role_operator_short') : t('dashboard.role_visitor_short')}</span>
                                          </span>
                                        </div>
                                        <button 
                                          className="btn btn-sm btn-outline-danger rounded-pill flex-shrink-0" 
                                          onClick={() => unshareEvent(shared.userId)}
                                          title="Αφαίρεση κοινοποίησης"
                                        >
                                          <i className="fas fa-times"></i>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <div className="fs-1 text-muted mb-3">
                    <i className="fas fa-calendar-plus"></i>
                  </div>
                  <h4 className="text-muted mb-2">{t('dashboard.select_event')}</h4>
                  <p className="text-muted">{t('dashboard.select_event_instruction')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }} onClick={() => {
          setShowShareModal(false);
          setShareEmail('');
          setShareRole('visitor');
          setError('');
        }}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px', background: 'var(--bg-secondary)' }}>
              <div className="modal-header border-0 pb-0">
                <div className="text-center w-100">
                  <div className="fs-1 text-primary mb-2">
                    <i className="fas fa-share-alt"></i>
                  </div>
                  <h4 className="modal-title fw-bold text-primary">{t('dashboard.share_modal_title')}</h4>
                  <p className="text-muted small mb-0">{t('dashboard.share_modal_desc', { name: currentEvent?.name })}</p>
                </div>
                <button type="button" className="btn-close" onClick={() => {
                  setShowShareModal(false);
                  setShareEmail('');
                  setShareRole('visitor');
                  setError('');
                }}></button>
              </div>
              <div className="modal-body pt-3">
                {error && (
                  <div className="alert alert-danger border-0" style={{ background: 'rgba(220, 53, 69, 0.1)', color: '#dc3545' }}>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-envelope me-1 text-primary"></i>
                    {t('dashboard.share_modal_email_label')}
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="fas fa-at text-muted"></i>
                    </span>
                    <input
                      type="email"
                      className="form-control border-start-0"
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                      placeholder="email@example.com"
                      onKeyPress={(e) => e.key === 'Enter' && shareEvent()}
                      style={{ background: 'rgba(255, 255, 255, 0.8)' }}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-user-tag me-1 text-primary"></i>
                    {t('dashboard.share_modal_role_label')}
                  </label>
                  <div className="row g-2">
                    <div className="col-6">
                      <div className={`card border-2 ${shareRole === 'visitor' ? 'border-info' : 'border-light'} cursor-pointer h-100`} 
                           onClick={() => setShareRole('visitor')}
                           style={{ cursor: 'pointer', transition: 'all 0.3s' }}>
                        <div className="card-body text-center p-3">
                          <div className={`fs-2 mb-2 ${shareRole === 'visitor' ? 'text-info' : 'text-muted'}`}>
                            <i className="fas fa-eye"></i>
                          </div>
                          <div className={`fw-semibold ${shareRole === 'visitor' ? 'text-info' : 'text-muted'}`}>{t('dashboard.role_visitor')}</div>
                          <small className="text-muted d-block">{t('dashboard.view_only')}</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className={`card border-2 ${shareRole === 'operator' ? 'border-warning' : 'border-light'} cursor-pointer h-100`} 
                           onClick={() => setShareRole('operator')}
                           style={{ cursor: 'pointer', transition: 'all 0.3s' }}>
                        <div className="card-body text-center p-3">
                          <div className={`fs-2 mb-2 ${shareRole === 'operator' ? 'text-warning' : 'text-muted'}`}>
                            <i className="fas fa-cog"></i>
                          </div>
                          <div className={`fw-semibold ${shareRole === 'operator' ? 'text-warning' : 'text-muted'}`}>{t('dashboard.role_operator')}</div>
                          <small className="text-muted d-block">{t('dashboard.full_access')}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => {
                  setShowShareModal(false);
                  setShareEmail('');
                  setShareRole('visitor');
                  setError('');
                }}>
                  <i className="fas fa-times me-1"></i>
                  {t('common.cancel')}
                </button>
                <button type="button" className="btn btn-primary rounded-pill px-4" onClick={shareEvent} disabled={loading}>
                  <i className="fas fa-share-alt me-1"></i>
                  {loading ? t('dashboard.share_modal_loading') : t('dashboard.share_button')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && currentEvent && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }} onClick={() => setShowEditModal(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px', background: 'var(--bg-secondary)' }}>
              <div className="modal-header border-0 pb-0">
                <div className="text-center w-100">
                  <div className="fs-1 text-primary mb-2">
                    <i className="fas fa-edit"></i>
                  </div>
                  <h4 className="modal-title fw-bold text-primary">{t('dashboard.edit_event_title')}</h4>
                  <p className="text-muted small mb-0">{t('dashboard.edit_event_subtitle')}</p>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body pt-3">
                {error && (
                  <div className="alert alert-danger border-0" style={{ background: 'rgba(220, 53, 69, 0.1)', color: '#dc3545' }}>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-tag me-1 text-primary"></i>
                    {t('dashboard.event_name_label')}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editEventName}
                    onChange={(e) => setEditEventName(toTitleCase(e.target.value))}
                    placeholder={t('dashboard.event_name_placeholder')}
                    style={{ background: 'rgba(255, 255, 255, 0.8)' }}
                  />
                </div>
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-calendar me-1 text-primary"></i>
                    {t('dashboard.event_date_label')}
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={editEventDate}
                    onChange={(e) => setEditEventDate(e.target.value)}
                    style={{ background: 'rgba(255, 255, 255, 0.8)' }}
                  />
                  <small className="text-muted d-block mt-2">{t('dashboard.event_date_optional')}</small>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowEditModal(false)}>
                  <i className="fas fa-times me-1"></i>
                  {t('common.cancel')}
                </button>
                <button type="button" className="btn btn-primary rounded-pill px-4" onClick={updateEventDetails} disabled={loading}>
                  <i className="fas fa-save me-1"></i>
                  {loading ? t('common.save') + '...' : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Event Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }} onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pb-0">
                <div className="text-center w-100">
                  <div className="fs-1 text-danger mb-2">
                    <i className="fas fa-exclamation-circle"></i>
                  </div>
                  <h4 className="modal-title fw-bold text-danger">{t('dashboard.delete_event_title')}</h4>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowDeleteConfirm(false)}></button>
              </div>
              <div className="modal-body pt-3 text-center">
                <p className="text-muted mb-0">
                  {t('dashboard.delete_event_confirm')}
                </p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowDeleteConfirm(false)}>
                  <i className="fas fa-times me-1"></i>
                  {t('common.cancel')}
                </button>
                <button type="button" className="btn btn-danger rounded-pill px-4" onClick={confirmDeleteEvent} disabled={loading}>
                  <i className="fas fa-trash-alt me-1"></i>
                  {loading ? t('tasks.deleting') : t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}